import classes from "./FormChoosePermitsOption.module.css";
import { useEffect, useState } from "react";
import { useCurrentDataContext } from "../../../contexts/CurrentDataContext";
import { PermitFormBodyKey } from "../../../contexts/interfaces";
import { FiCalendar } from "react-icons/fi";

import { useTranslation } from "react-i18next";

// Datepicker imports
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLanguageContext } from "../../../contexts/LanguageContext";
// Import dateLocales utility to ensure locale is registered correctly
import "../../../utils/dateLocales";

type CheckProps = {
  id: number;
  fullTitle: string;
};

const FormChoosePermitsOption = ({ id, fullTitle }: CheckProps) => {
  const { currentData, setCurrentData } = useCurrentDataContext();
  const { language } = useLanguageContext();
  const [chosenPermit, setChosenPermit] = useState<PermitFormBodyKey>();

  // Set translation
  const { t } = useTranslation();

  useEffect(() => {
    const permit = currentData?.permits?.find((item) => item.permitId === id);
    setChosenPermit(permit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData]);

  // ** doChange function gets key of permit object as a string and new value
  // which allows it to change demonstrationAssessor1 (name of approver) and demonstrationAssessor1Date (from datepicker)

  const doChange = (
    key: keyof PermitFormBodyKey,
    value: string | Date | null
  ) => {
    // Add to asked permits
    let modifiedData = currentData;

    // Get the index of correct permit
    const index = modifiedData?.permits?.findIndex(
      (item) => item.permitId === id
    );

    if (modifiedData?.permits && index !== -1 && index !== undefined) {
      // Get modifiedPermit by index
      let modifiedPermit: PermitFormBodyKey = modifiedData?.permits[index];

      if (value && typeof value !== "string") {
        // Timezone modification, otherwise day is 1 day off
        const date =
          new Date(value.getTime() - value.getTimezoneOffset() * 60000) || null;
        modifiedPermit[key] = date.toISOString();
      } else {
        // Set new value to field that's being changed (key)
        modifiedPermit[key] = value;
      }

      // Update modified data permits with new modifiedPermit
      modifiedData.permits[index] = modifiedPermit;

      // Update modified data to currentData
      setCurrentData({ ...modifiedData });
    }
  };

  const changeChecked = (id: number) => {
    if (!currentData?.permits) {
      return;
    }

    // Add to asked permits
    let modifiedPermits = [...currentData.permits];

    // Get the index of correct permit
    const index = modifiedPermits.findIndex((item) => item.permitId === id);

    if (
      modifiedPermits &&
      index !== -1 &&
      index !== undefined &&
      modifiedPermits[index].type === "medicineOther"
    ) {
      // Remove "other permit" from array
      modifiedPermits.splice(index, 1);
      // Update modified data to currentData
      setCurrentData({
        ...currentData,
        permits: [...modifiedPermits],
      });
    } else if (modifiedPermits && index !== -1 && index !== undefined) {
      // Get current true or false
      const currentBoolean = modifiedPermits[index].isChecked;

      // Set the opposite
      modifiedPermits[index].isChecked = !currentBoolean;

      // Update modified data to currentData
      setCurrentData({
        ...currentData,
        permits: [...modifiedPermits],
      });
    }
  };

  if (chosenPermit && currentData) {
    return (
      <div className={classes.formChoosePermitsOptionContainer}>
        <div className={classes.formChoosePermitsRow2}>
          <input
            className={classes.formChoosePermitsCheckbox}
            type="checkbox"
            checked={chosenPermit?.isChecked}
            onChange={() => changeChecked(id)}
          />
          <div className={classes.formChoosePermitsCheckboxText}>
            {fullTitle}
          </div>
        </div>
        {chosenPermit?.isChecked ? (
          <div className={classes.formChoosePermitsRowContainer}>
            <div className={classes.formChoosePermitsRow}>
              <div>
                <div className={classes.formChoosePermitsReceiver}>
                  1.{" "}
                  {String(t("formChoosePermitsOption.demonstrationAssessor"))}
                </div>
                <input
                  className={classes.formChoosePermitsInput}
                  value={chosenPermit?.demonstrationAssessor1 || ""}
                  onChange={(e) =>
                    doChange("demonstrationAssessor1", e.target.value)
                  }
                />
              </div>
              <div>
                <div className={classes.formChoosePermitsDateTitle}>
                  {String(t("formChoosePermitsOption.date"))}
                </div>
                <div className={classes.formChoosePermitsRow}>
                  <DatePicker
                    locale={language}
                    selected={
                      typeof chosenPermit.demonstrationAssessor1Date ===
                      "string"
                        ? new Date(chosenPermit.demonstrationAssessor1Date)
                        : undefined
                    }
                    // date = typeof (date) === "string" ? new Date(date) : undefined;
                    peekNextMonth
                    dateFormat="dd.MM.yyyy"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    onChange={(date) => {
                      doChange("demonstrationAssessor1Date", date);
                    }}
                    maxDate={new Date()}
                  />
                  <FiCalendar className={classes.calendarIcon} />
                </div>
              </div>
            </div>
            <div className={classes.formChoosePermitsRow2}>
              <div>
                <div className={classes.formChoosePermitsReceiver}>
                  2.{" "}
                  {String(t("formChoosePermitsOption.demonstrationAssessor"))}
                </div>

                <input
                  className={classes.formChoosePermitsInput}
                  value={chosenPermit.demonstrationAssessor2 || ""}
                  onChange={(e) =>
                    doChange("demonstrationAssessor2", e.target.value)
                  }
                />
              </div>
              <div>
                <div className={classes.formChoosePermitsDateTitle}>
                  {String(t("formChoosePermitsOption.date"))}
                </div>
                <div className={classes.formChoosePermitsRow}>
                  <DatePicker
                    locale={language}
                    selected={
                      typeof chosenPermit.demonstrationAssessor2Date ===
                      "string"
                        ? new Date(chosenPermit.demonstrationAssessor2Date)
                        : undefined
                    }
                    peekNextMonth
                    dateFormat="dd.MM.yyyy"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    onChange={(date) => {
                      doChange("demonstrationAssessor2Date", date);
                    }}
                    maxDate={new Date()}
                  />
                  <FiCalendar className={classes.calendarIcon} />
                </div>
              </div>
            </div>

            <div className={classes.formChoosePermitsRow2}>
              <div>
                <div className={classes.formChoosePermitsReceiver}>
                  3.{" "}
                  {String(t("formChoosePermitsOption.demonstrationAssessor"))}
                </div>

                <input
                  className={classes.formChoosePermitsInput}
                  value={chosenPermit.demonstrationAssessor3 || ""}
                  onChange={(e) =>
                    doChange("demonstrationAssessor3", e.target.value)
                  }
                />
              </div>
              <div>
                <div className={classes.formChoosePermitsDateTitle}>
                  {String(t("formChoosePermitsOption.date"))}
                </div>
                <div className={classes.formChoosePermitsRow}>
                  <DatePicker
                    locale={language}
                    selected={
                      typeof chosenPermit.demonstrationAssessor3Date ===
                      "string"
                        ? new Date(chosenPermit.demonstrationAssessor3Date)
                        : undefined
                    }
                    peekNextMonth
                    dateFormat="dd.MM.yyyy"
                    showYearDropdown
                    showMonthDropdown
                    dropdownMode="select"
                    onChange={(date) => {
                      doChange("demonstrationAssessor3Date", date);
                    }}
                    maxDate={new Date()}
                  />
                  <FiCalendar className={classes.calendarIcon} />
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  } else {
    return null;
  }
};

export default FormChoosePermitsOption;
